import { GridActionsCellItem, GridColumns, GridNativeColTypes, useGridApiRef } from '@mui/x-data-grid-pro'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { invalidatePositionCache, useDeletePosition } from '../../../../api/positions'
import { IPosition, IPositionType, IPositionWithDrawing } from '../../../../generated-types'
import { toast } from 'react-toastify'
import { MdDelete, MdEdit, MdEditNote } from 'react-icons/md'
import { useDialogCtx, useMooringsCtx } from '../../../../hooks/context-hooks'
import {
  CreatePositionDeviationDialog,
  ICreateDeviationDialogProps,
} from '../../deviations/CreatePositionDeviationDialog'
import { IDeviationFormWizardType } from '../../../../generated-types/form-wizard'
import { IDeviationType } from '../../../../generated-types/deviation-type'
import { UpdatePositionDialog } from '../../positions/UpdatePositionDialog'
import { PositionsTableStaticColumns } from '../Helpers/PositionsTableHelper'
import { AppRoutesNavigation } from '../../../../app-routes'
import PositionInformationContainer from '../PositionInformationContainer'
import { useNavigation } from 'src/context/NavigationContext'
import { CONNECTED_POSITIONS_EXIST, POSITIONS_CONNECTED_TO_OTHER_POSITIONS, POSITION_HAS_EQUIPMENT } from 'src/constant'
import { useGetPositionDrawingInMooring } from 'src/api/position-drawing'
import { Checkbox } from '@mui/material'
import { CustomStyledTable as CustomStyledTableComponent, IPositionTable } from './PositionTable.Style'
import { coreUtils } from 'src/utils'
import { invalidateDocumentCategory } from 'src/api/users/document-category'
import { invalidateLocalityCache } from 'src/api/localities'
import { invalidateMooringCache } from 'src/api/moorings'
import { useColumnWidthMeasurement } from '../positionTableHelper'
import { isGeometryPositionType } from 'src/utils/position-geometry'

export const CustomStyledTable = CustomStyledTableComponent

export const PositionTable: React.FC<IPositionTable> = ({
  customerId,
  localityId,
  mooringId,
  positions,
  checkboxSelection = false,
  onColumnWidthsChange,
}) => {
  const apiRef = useGridApiRef()
  const { setSelectedPosition, setSelectedPositions, selectedPositions } = useMooringsCtx()
  const { t } = useTranslation(['mooring', 'common', 'position', 'deviation'])
  const { showDialog, showConfirmDialog } = useDialogCtx()
  const [expandedPositionId, setExpandedPositionId] = useState<number>()
  const { navigateTo } = useNavigation()
  const { data: availablePositionDrawings, isLoading } = useGetPositionDrawingInMooring(
    customerId as EntityId,
    mooringId as EntityId,
  )

  useEffect(() => {
    const position = positions.find(x => x.id === expandedPositionId)
    setSelectedPosition(position)
  }, [expandedPositionId, positions])

  const { mutateAsync: deletePosition } = useDeletePosition(
    customerId as EntityId,
    localityId as EntityId,
    mooringId as EntityId,
  )

  const positionDetailPanelContent = (row: IPosition) => {
    return (
      <PositionInformationContainer
        customerId={customerId}
        positionId={row?.id}
        localityId={localityId}
        mooringId={mooringId}
        position={row as IPositionWithDrawing}
        availableDrawings={availablePositionDrawings}
        isLoadingTemplates={isLoading}
      />
    )
  }

  const onUpdate = useCallback(
    async (position: IPosition) => {
      showDialog(
        UpdatePositionDialog,
        {
          opts: {
            maxWidth: 'md',
            fullWidth: true,
          },
          componentProps: {
            customerId,
            localityId,
            mooringId,
            positionId: position.id,
            position,
          },
        },
        undefined,
        true,
      )
    },
    [showDialog, t, customerId, localityId, mooringId],
  )

  const onDelete = useCallback(
    async (id: EntityId) => {
      showConfirmDialog(
        t('deletePosition', { ns: 'position' }),
        t('areYouSureDeletePosition', { ns: 'position' }),
        {
          acceptText: t('yes', { ns: 'common' }),
          cancelText: t('no', { ns: 'common' }),
        },
        async pop => {
          await deletePosition(id, {
            onSuccess: data => {
              toast(t(data.data, { ns: 'common-api-message' }), { type: 'success' })
              invalidatePositionCache.getPositions(customerId as EntityId, localityId, mooringId)
              invalidatePositionCache.getPositionsWithMinimalData(customerId, localityId, mooringId)
              invalidateDocumentCategory.getDocumentCategoriesWithDocuments(customerId)
              invalidateLocalityCache.getDocumentsCount(customerId, localityId)
              invalidateLocalityCache.getLocalityDeviationCounts(customerId, localityId)
              invalidateLocalityCache.getLocalityWithActivityCounts(customerId, localityId)
              invalidateMooringCache.getMooringsForMap(customerId, localityId)
              invalidateMooringCache.getGetMooring(customerId, localityId, mooringId, { isWithPositionData: true })
              invalidateLocalityCache.getMaintenanceTypesCount(customerId, localityId)
              invalidateLocalityCache.getMaintenances(customerId, localityId)
              invalidatePositionCache.getConnectedPositionsForDropdown(customerId, localityId, mooringId)
              invalidateMooringCache.getMapSvg(customerId, localityId, mooringId)
              pop()
            },
            onError: error => {
              if (error['data'] == POSITIONS_CONNECTED_TO_OTHER_POSITIONS) {
                toast.error(t('positionConnectedToOtherPositions', { ns: 'position' }))
              }
              if (error['data'] == CONNECTED_POSITIONS_EXIST) {
                toast.error(t('ConnectedPositionsExist', { ns: 'position' }))
              }
              if (error['data'] == POSITION_HAS_EQUIPMENT) {
                toast.error(t('positionHasEquipment', { ns: 'position' }))
              }
            },
          })
        },
      )
    },
    [customerId, localityId, mooringId, deletePosition, showConfirmDialog, t],
  )

  const onAddDeviationForPosition = useCallback(
    async position => {
      showDialog(
        CreatePositionDeviationDialog,
        {
          title: t('addNewDeviation', { ns: 'deviation' }),
          opts: {
            maxWidth: 'md',
            fullWidth: true,
          },
          componentProps: {
            customerId,
            localityId,
            formWizard: IDeviationFormWizardType.Position,
            disableSteps: false,
            type: IDeviationType.Position,
            position,
            mooringId,
          } as ICreateDeviationDialogProps,
        },
        undefined,
        true,
      )
    },
    [customerId, localityId, showDialog, t, mooringId],
  )

  const onRowClick = (positionId: string | number) => {
    if (localityId)
      navigateTo(
        AppRoutesNavigation.Localities.position(customerId, localityId as EntityId, mooringId as EntityId, positionId),
      )
  }

  const getActionItems = useCallback(
    params => {
      const removeItem = (
        <GridActionsCellItem
          key={params.id}
          label={t('remove', { ns: 'common' })}
          icon={<MdDelete size={24} />}
          onClick={() => {
            onDelete(params.id)
          }}
          color="primary"
          showInMenu
        />
      )
      if (isGeometryPositionType(params.row.type)) return [removeItem]
      return [
        <GridActionsCellItem
          key={params.id}
          label={t('edit', { ns: 'common' })}
          icon={<MdEdit size={24} />}
          onClick={() => {
            onUpdate(params.row)
          }}
          color="primary"
          showInMenu
        />,
        removeItem,
        <GridActionsCellItem
          key={params.id}
          label={t('addDeviation', { ns: 'deviation' })}
          icon={<MdEditNote size={24} />}
          onClick={() => {
            onAddDeviationForPosition(params.row)
          }}
          color="primary"
          showInMenu
        />,
      ]
    },
    [onUpdate, onDelete, onAddDeviationForPosition, t],
  )

  const handleSelectCheckbox = useCallback(
    (e, row) => {
      setSelectedPositions((prev: IPosition[]) => {
        return coreUtils.handleRowSelectFunc<IPosition>(prev, row, positions, e)
      })
    },
    [positions],
  )

  const columns: GridColumns = useMemo(
    () => [
      ...(checkboxSelection
        ? [
            {
              field: 'id',
              headerName: '',
              type: 'actions',
              width: 25,
              sx: {
                paddingLeft: '0px',
              },
              renderCell: ({ row }) => {
                return (
                  <Checkbox
                    sx={{ padding: '0px' }}
                    checked={selectedPositions.some(x => x.id == row.id)}
                    onClick={e => handleSelectCheckbox(e, row)}
                  />
                )
              },
            },
          ]
        : []),
      ...PositionsTableStaticColumns,
      {
        field: 'actions',
        headerName: t('actions', { ns: 'common' }),
        type: 'actions' as GridNativeColTypes,
        minWidth: 60,
        cellClassName: 'ml-4',
        getActions: getActionItems,
      },
    ],
    [
      customerId,
      localityId,
      mooringId,
      t,
      getActionItems,
      availablePositionDrawings,
      isLoading,
      selectedPositions,
      checkboxSelection,
    ],
  )

  // Measure column widths and sync with parent component
  useColumnWidthMeasurement(apiRef, positions.length, checkboxSelection, onColumnWidthsChange)

  return (
    <CustomStyledTable
      apiRef={apiRef}
      id="mooring-positions-table"
      columns={columns}
      data={positions}
      pageSize={10}
      enableToolbar={true}
      getDetailPanelContent={({ row }) =>
        row.type != IPositionType.Cage && !isGeometryPositionType(row.type)
          ? positionDetailPanelContent(row)
          : undefined
      }
      getDetailPanelHeight={() => 'auto'}
      getRowHeight={() => 'auto'}
      getRowClassName={params =>
        `${params.indexRelativeToCurrentPage % 2 == 0 ? 'even' : 'odd'}${
          isGeometryPositionType(params.row.type) ? ' non-clickable-row' : ''
        }`
      }
      onDetailPanelExpandedRowIdsChange={async ids => {
        if (ids.length > 0) {
          const id = [...ids].pop()
          setExpandedPositionId(id as number)
        } else {
          setExpandedPositionId(undefined)
        }
      }}
      detailPanelExpandedRowIds={expandedPositionId ? [expandedPositionId] : []}
      onRowClick={row => {
        if (isGeometryPositionType(row?.row?.type)) return
        onRowClick(row?.id)
      }}
      sortModel={[
        {
          field: 'name',
          sort: 'asc',
        },
      ]}
      className={'position-table'}
    />
  )
}

export default PositionTable
